import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { Phone, MessageSquare, X, MapPin, ChevronRight, ChevronDown, Check, Sparkles, Copy, Loader2 } from 'lucide-react';
import { tplMeta, KIND_META, chMeta } from './templateMeta';
import { DraftReply } from './DraftReply';

const fmt = (p) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: p % 1 === 0 ? 0 : 2 }).format(p || 0);
const fmtDay = (d) => {
  if (!d) return '';
  try { return new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }
  catch { return d; }
};
const FONT_H = { fontFamily: '"Cabinet Grotesk", system-ui, sans-serif' };
const FONT_B = { fontFamily: 'Manrope, system-ui, sans-serif' };

const Label = ({ children, count, tone, dotCls }) => (
  <div className="flex items-baseline gap-3 mb-4">
    {dotCls && <span className={`w-2 h-2 rounded-full ${dotCls}`} />}
    <h3 className={`text-[24px] font-black tracking-tighter leading-none ${tone || 'text-white'}`} style={FONT_H}>{children}</h3>
    {count > 0 && (
      <span className={`text-sm font-black tabular-nums ${tone || 'text-zinc-500'} opacity-70`} style={FONT_H}>{count}</span>
    )}
  </div>
);

function EventRow({ e, onOpen }) {
  return (
    <div className="flex items-center gap-4 bg-[#111111] border border-white/5 rounded-2xl px-5 py-4 cursor-pointer active:scale-[0.98] transition-transform"
         onClick={() => onOpen(e.proposal_id)} data-testid={`today-event-${e.proposal_id}`}>
      <div className="min-w-0 flex-1">
        <p className="text-white text-[16px] font-bold tracking-tight truncate" style={FONT_H}>{e.client_name}</p>
        <p className="text-zinc-500 text-[13px] mt-0.5 truncate" style={FONT_B}>{e.event_type || 'Event'}{e.event_time ? ` · ${e.event_time}` : ''}{e.event_date ? ` · ${fmtDay(e.event_date)}` : ''}</p>
        {e.event_location && <p className="text-zinc-600 text-xs truncate flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3 flex-shrink-0" />{e.event_location}</p>}
      </div>
      <span className="text-white text-sm font-bold tabular-nums flex-shrink-0" style={FONT_H}>{fmt(e.total)}</span>
      {e.client_phone && (
        <a href={`tel:${e.client_phone}`} onClick={(ev) => ev.stopPropagation()}
           className="w-11 h-11 flex items-center justify-center rounded-full bg-white/5 text-zinc-400 active:scale-95 flex-shrink-0 transition-transform" aria-label="Call">
          <Phone className="w-4 h-4" />
        </a>
      )}
    </div>
  );
}

function PriorityRow({ pr, onOpen, onDraft, onHandled }) {
  const meta = KIND_META[pr.kind] || KIND_META.respond;
  const sub = [pr.event_type, fmtDay(pr.event_date), pr.about].filter(Boolean).join(' · ');
  const money = pr.amount != null ? fmt(pr.amount) : (pr.total ? fmt(pr.total) : null);
  const borderCls = pr.kind === 'respond'
    ? 'border-violet-500/40 shadow-[0_0_0_1px_rgba(139,92,246,0.15)]'
    : pr.kind === 'unpaid' ? 'border-amber-500/30'
    : pr.kind === 'balance' ? 'border-orange-500/30'
    : pr.kind === 'unsigned' ? 'border-cyan-500/30'
    : 'border-white/5';
  return (
    <div className={`bg-[#111111] border ${borderCls} rounded-2xl px-4 py-4 cursor-pointer active:scale-[0.99] transition-transform`}
         onClick={() => onOpen(pr.proposal_id)} data-testid={`priority-${pr.proposal_id}-${pr.kind}`}>
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-white text-[15px] font-bold tracking-tight truncate min-w-0" style={FONT_H}>{pr.client_name}</p>
        {money && (
          <span className={`text-sm font-bold tabular-nums flex-shrink-0 ${pr.amount != null ? meta.text : 'text-zinc-300'}`} style={FONT_H}>{money}</span>
        )}
      </div>
      {(sub || pr.detail) && (
        <p className="text-zinc-400 text-[12px] mt-0.5 truncate" style={FONT_B}>{sub || pr.detail}</p>
      )}
      {pr.snippet && (
        <p className="text-zinc-300 text-[12px] italic leading-relaxed mt-2 line-clamp-2" style={FONT_B}>“{pr.snippet}”</p>
      )}
      <div className="flex items-center gap-2 mt-3">
        <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-md font-black ${meta.bg} ${meta.text}`} style={FONT_B}>{meta.label}</span>
        {pr.channel && (
          <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-md font-black ${chMeta(pr.channel).bg} ${chMeta(pr.channel).text}`} style={FONT_B}>{chMeta(pr.channel).label}</span>
        )}
        {pr.waiting && <span className="text-zinc-500 text-[10px] font-semibold" style={FONT_B}>waiting {pr.waiting}</span>}
        <div className="flex-1" />
        {pr.kind === 'respond' ? (
          <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => onDraft(pr)}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-white text-black text-[11px] font-bold active:scale-95 transition-transform"
                    style={FONT_B} data-testid={`priority-respond-${pr.proposal_id}`}>
              <Sparkles className="w-3 h-3" /> Respond
            </button>
            <button onClick={() => onHandled(pr.proposal_id)}
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-white/5 text-zinc-500 hover:text-emerald-400 active:scale-95 transition-transform"
                    data-testid={`priority-done-${pr.proposal_id}`} aria-label="Mark responded">
              <Check className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <ChevronRight className="w-4 h-4 text-zinc-500 flex-shrink-0" />
        )}
      </div>
    </div>
  );
}

function QueuedText({ t, open, onToggle, onDismiss }) {
  const meta = tplMeta(t.template);
  const [text, setText] = useState(t.message);
  const [busy, setBusy] = useState(false);

  const rewrite = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/api/admin/leads/${t.proposal_id}/draft-reply`, { intent: t.template, base_message: text });
      setText(res.draft);
      toast.success('Rewritten by OZ');
    } catch { toast.error('Rewrite failed'); }
    setBusy(false);
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(text); toast.success('Copied'); }
    catch { toast.error('Copy failed'); }
  };

  return (
    <div className="bg-[#111111] border border-white/5 rounded-2xl overflow-hidden" data-testid={`queued-text-${t.id}`}>
      <button onClick={onToggle} className="w-full flex items-center gap-3 px-4 py-3.5 text-left" data-testid={`queued-toggle-${t.id}`}>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-white text-[14px] font-bold tracking-tight truncate" style={FONT_H}>{t.client_name || t.to}</p>
            <span className={`text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded font-black flex-shrink-0 ${meta.bg} ${meta.text}`} style={FONT_B}>{meta.label}</span>
          </div>
          {!open && <p className="text-zinc-600 text-[12px] truncate mt-0.5" style={FONT_B}>{text}</p>}
        </div>
        <ChevronDown className={`w-4 h-4 text-zinc-600 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-4">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4}
                    className="w-full bg-black/40 border border-white/10 rounded-xl px-3.5 py-3 text-[13px] text-zinc-200 leading-relaxed focus:outline-none focus:border-white/30 resize-none mb-3"
                    style={FONT_B} data-testid={`queued-edit-${t.id}`} />
          <div className="flex flex-wrap items-center gap-2">
            <a href={`sms:${t.to}?&body=${encodeURIComponent(text)}`}
               className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-black text-sm font-bold active:scale-95 transition-transform"
               style={FONT_B} data-testid={`send-text-${t.id}`}>
              <MessageSquare className="w-4 h-4" /> Send
            </a>
            {t.proposal_id && (
              <button onClick={rewrite} disabled={busy}
                      className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-blue-500/15 text-blue-400 text-xs font-bold active:scale-95 disabled:opacity-40 transition-transform"
                      style={FONT_B} data-testid={`rewrite-text-${t.id}`}>
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} AI Rewrite
              </button>
            )}
            <button onClick={copy}
                    className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white/5 text-zinc-400 text-xs font-bold active:scale-95 transition-transform"
                    style={FONT_B} data-testid={`copy-text-${t.id}`}>
              <Copy className="w-3.5 h-3.5" /> Copy
            </button>
            <button onClick={() => onDismiss(t.id)}
                    className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white/5 text-zinc-500 text-xs font-bold active:scale-95 transition-transform"
                    style={FONT_B} data-testid={`dismiss-text-${t.id}`}>
              <X className="w-3.5 h-3.5" /> Dismiss
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function UnpaidRow({ u, onOpen }) {
  return (
    <div className="flex items-center gap-3 bg-[#111111] border border-white/5 rounded-2xl px-4 py-3.5 cursor-pointer active:scale-[0.99] transition-transform"
         onClick={() => onOpen(u.proposal_id)} data-testid={`unpaid-${u.proposal_id}`}>
      <div className="min-w-0 flex-1">
        <p className="text-white text-[14px] font-bold tracking-tight truncate" style={FONT_H}>{u.client_name}</p>
        <p className="text-zinc-500 text-[12px] mt-0.5 truncate" style={FONT_B}>
          {u.event_date ? fmtDay(u.event_date) : '—'}
          {u.paid > 0 ? ` · paid ${fmt(u.paid)} of ${fmt(u.total)}` : ` · ${fmt(u.total)} total`}
        </p>
      </div>
      <span className="text-amber-400 text-sm font-bold tabular-nums flex-shrink-0" style={FONT_H}>{fmt(u.balance)}</span>
      <ChevronRight className="w-4 h-4 text-zinc-700 flex-shrink-0" />
    </div>
  );
}

export const TodayView = ({ onOpenLead }) => {
  const [data, setData] = useState(null);
  const [draftFor, setDraftFor] = useState(null);
  const [openText, setOpenText] = useState(null);

  const load = useCallback(async () => {
    try { setData(await api.get('/api/admin/today')); }
    catch (err) { console.error('Failed to load today view', err); toast.error('Failed to load'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const dismiss = async (id) => {
    try { await api.post(`/api/admin/notifications/${id}/dismiss`, {}); load(); }
    catch (err) { toast.error('Failed to dismiss'); }
  };

  const markHandled = async (pid) => {
    try { await api.post(`/api/admin/leads/${pid}/mark-responded`, {}); toast.success('Handled'); load(); }
    catch (err) { toast.error('Failed'); }
  };

  if (!data) return <p className="text-zinc-600 text-sm py-16 text-center" style={FONT_B}>Loading…</p>;

  const priorities = data.priorities || [];
  const respondList = priorities.filter(p => p.kind === 'respond');
  const moneyList = priorities.filter(p => p.kind === 'unpaid' || p.kind === 'balance');
  const contractList = priorities.filter(p => p.kind === 'unsigned');
  const vendorList = priorities.filter(p => p.kind === 'vendor_stalled');
  const unpaid = data.unpaid || [];
  const unpaidTotal = unpaid.reduce((s, u) => s + u.balance, 0);
  const allClear = priorities.length === 0 && data.today_events.length === 0 && data.queued_texts.length === 0 && data.upcoming_events.length === 0;
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const dateLine = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  const summaryBits = [];
  if (respondList.length) summaryBits.push(`${respondList.length} need${respondList.length === 1 ? 's' : ''} reply`);
  if (moneyList.length) summaryBits.push(`${moneyList.length} money`);
  if (contractList.length) summaryBits.push(`${contractList.length} contract${contractList.length === 1 ? '' : 's'}`);
  if (vendorList.length) summaryBits.push(`${vendorList.length} vendor silent`);
  if (data.today_events.length) summaryBits.push(`${data.today_events.length} today`);

  return (
    <div className="space-y-10" data-testid="today-view">
      <div>
        <h2 className="text-white text-[32px] font-black tracking-tighter leading-none" style={FONT_H}>{greeting}.</h2>
        <p className="text-zinc-500 text-sm mt-2" style={FONT_B}>
          {dateLine}{summaryBits.length ? ` · ${summaryBits.join(' · ')}` : ' · all clear'}
        </p>
      </div>

      {/* Overview strip — always visible at top for a money-glance */}
      <div className="grid grid-cols-3 gap-3" data-testid="today-stats">
        {[['Unpaid', fmt(unpaidTotal), unpaidTotal > 0 ? 'text-amber-400' : 'text-zinc-600'],
          ['Events Today', data.today_events.length, data.today_events.length > 0 ? 'text-blue-400' : 'text-zinc-600'],
          ['Ready to Send', data.queued_texts.length, data.queued_texts.length > 0 ? 'text-emerald-400' : 'text-zinc-600']].map(([label, val, cls]) => (
          <div key={label} className="bg-[#111111] border border-white/5 rounded-2xl py-4 px-2 text-center">
            <p className={`text-[19px] font-black tracking-tighter tabular-nums ${cls}`} style={FONT_H}>{val}</p>
            <p className="text-zinc-500 text-[9px] uppercase tracking-[0.15em] font-semibold mt-1" style={FONT_B}>{label}</p>
          </div>
        ))}
      </div>

      {/* Events Today — priority #1 if today */}
      {data.today_events.length > 0 && (
        <div data-testid="today-events">
          <Label count={data.today_events.length} tone="text-blue-400" dotCls="bg-blue-400">Events Today</Label>
          <div className="space-y-3">{data.today_events.map(e => <EventRow key={e.proposal_id} e={e} onOpen={onOpenLead} />)}</div>
        </div>
      )}

      {/* Priorities: grouped by kind, everything visible */}
      {respondList.length > 0 && (
        <div data-testid="priorities-respond">
          <Label count={respondList.length} tone="text-violet-400" dotCls="bg-violet-400">Needs Reply</Label>
          <div className="space-y-2.5">
            {respondList.map(pr => (
              <PriorityRow key={`${pr.proposal_id}-${pr.kind}`} pr={pr} onOpen={onOpenLead}
                           onDraft={(p) => setDraftFor(p)} onHandled={markHandled} />
            ))}
          </div>
        </div>
      )}

      {data.queued_texts.length > 0 && (
        <div data-testid="today-texts">
          <Label count={data.queued_texts.length} tone="text-emerald-400" dotCls="bg-emerald-400">Ready to Send</Label>
          <p className="text-zinc-500 text-xs -mt-2 mb-4 leading-relaxed" style={FONT_B}>
            Texts your automations composed — tap one to review and send from your phone.
          </p>
          <div className="space-y-2.5">
            {data.queued_texts.map(t => (
              <QueuedText key={t.id} t={t} open={openText === t.id}
                          onToggle={() => setOpenText(openText === t.id ? null : t.id)}
                          onDismiss={dismiss} />
            ))}
          </div>
        </div>
      )}

      {moneyList.length > 0 && (
        <div data-testid="priorities-money">
          <Label count={moneyList.length} tone="text-amber-400" dotCls="bg-amber-400">Money</Label>
          <div className="space-y-2.5">
            {moneyList.map(pr => (
              <PriorityRow key={`${pr.proposal_id}-${pr.kind}`} pr={pr} onOpen={onOpenLead}
                           onDraft={(p) => setDraftFor(p)} onHandled={markHandled} />
            ))}
          </div>
        </div>
      )}

      {contractList.length > 0 && (
        <div data-testid="priorities-contracts">
          <Label count={contractList.length} tone="text-cyan-400" dotCls="bg-cyan-400">Contracts</Label>
          <div className="space-y-2.5">
            {contractList.map(pr => (
              <PriorityRow key={`${pr.proposal_id}-${pr.kind}`} pr={pr} onOpen={onOpenLead}
                           onDraft={(p) => setDraftFor(p)} onHandled={markHandled} />
            ))}
          </div>
        </div>
      )}

      {vendorList.length > 0 && (
        <div data-testid="priorities-vendors">
          <Label count={vendorList.length} tone="text-rose-400" dotCls="bg-rose-400">Vendor Silent</Label>
          <div className="space-y-2.5">
            {vendorList.map(pr => (
              <PriorityRow key={`${pr.proposal_id}-${pr.kind}`} pr={pr} onOpen={onOpenLead}
                           onDraft={(p) => setDraftFor(p)} onHandled={markHandled} />
            ))}
          </div>
        </div>
      )}

      {allClear && (
        <div className="text-center py-8" data-testid="today-all-clear">
          <img src="https://images.unsplash.com/photo-1699412947867-d750083c5839?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
               alt="Resting panther" className="w-full max-w-md mx-auto rounded-3xl opacity-60 mb-6 object-cover aspect-video" />
          <p className="text-white text-xl font-bold tracking-tight" style={FONT_H}>All caught up.</p>
          <p className="text-zinc-500 text-sm mt-1" style={FONT_B}>Nothing needs your attention right now.</p>
        </div>
      )}

      {data.upcoming_events.length > 0 && (
        <div data-testid="today-upcoming">
          <Label count={data.upcoming_events.length} tone="text-zinc-300" dotCls="bg-zinc-400">Next 7 Days</Label>
          <div className="space-y-3">{data.upcoming_events.map(e => <EventRow key={e.proposal_id} e={e} onOpen={onOpenLead} />)}</div>
        </div>
      )}

      {draftFor && (
        <DraftReply
          proposalId={draftFor.proposal_id}
          clientName={draftFor.client_name}
          clientPhone={draftFor.client_phone}
          hasDm={!!draftFor.channel && draftFor.channel !== 'sms'}
          onClose={() => setDraftFor(null)}
          onDone={() => { setDraftFor(null); load(); }}
        />
      )}
    </div>
  );
};
